import React, { useState, useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Outlet, useLocation, useParams } from '@tanstack/react-router';
import { RouterService } from '@piwikpro/router-crate';
import {
  Layout,
  Card,
  SegmentedSelection,
  Stack,
  Button,
  Icon,
  Text,
  withTranslation,
  TFunction,
} from '@piwikpro/ui-components';
import { withInject, errorBoundary } from '@piwikpro/platform';
import { ContainerSettings } from '@piwikpro/container-settings-crate/ContainerSettings';
import { Organization } from '@piwikpro/organization-crate/Organization';
import { AdministrationState, STATE_ROOT } from '../../../../redux';

const mapStateToProps = (state: AdministrationState) => ({
  app: state[STATE_ROOT].apps.details.settings.settings,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  Title: typeof React.Component
  containerSettings: ContainerSettings
  routerService: RouterService
  organization: Organization
}

interface Props extends ReduxProps, InjectedProps {
  t: TFunction
}

const Installation: React.FC<Props> = ({
  app,
  t,
  Title,
  containerSettings,
  organization,
  routerService,
}) => {
  const params = useParams({ from: '/_secured/administration/apps/$appId' });
  const { appId } = params;
  const { pathname } = useLocation();

  const [installationSection, setInstallationSection] = useState<string>(() => (
    pathname.includes('/installation/sites') || /\/installation\/?$/.test(pathname)
      ? 'sites'
      : 'mobileApps'
  ));

  const selectionButtons = [
    {
      name: t('apps.details.installation.sites.button'),
      value: 'sites',
    },
    {
      name: t('apps.details.installation.mobile-apps.button'),
      value: 'mobileApps',
    },
  ];

  useEffect(() => {
    if (!app) {
      return;
    }

    const fetchData = async () => {
      try {
        await containerSettings.getAppInstallationCode(app.id);
        await containerSettings.getAppSetting(app.id, 'static_resources_domain');
        await containerSettings.getAppSetting(app.id, 'tracking_domain');
        await containerSettings.getAppSetting(app.id, 'tracking_endpoint_path');
        await organization.fetchDetails();
      // eslint-disable-next-line no-empty
      } catch (err) { }
    };
    fetchData();
  }, [app, containerSettings, organization]);

  const handleInstallationViewChange = (newValue: string) => {
    if (!newValue) {
      return;
    }

    if (newValue === selectionButtons[0].value) {
      routerService.push({
        to: '/administration/apps/$appId/installation/sites',
        params: { appId },
      });
    } else if (newValue === selectionButtons[1].value) {
      routerService.push({
        to: '/administration/apps/$appId/installation/mobile-apps',
        params: { appId },
      });
    }

    setInstallationSection(newValue);
  };

  if (!app) {
    return null;
  }

  return (
    <Title part={t('apps.details.tabs.installation')}>
      <Layout>
        <Stack vertical>
          <Stack.Item fill>
            <SegmentedSelection
              value={installationSection}
              fullWidth
              items={selectionButtons}
              onChange={handleInstallationViewChange}
            />
          </Stack.Item>
          <Stack.Item fill>
            <Outlet />
          </Stack.Item>
          <Stack.Item fill>
            <Stack vertical>
              <Stack.Item fill>
                <div style={{ marginTop: '8px' }}>
                  <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">
                    {t('apps.details.installation.help.debugger')}
                  </Text>
                </div>
              </Stack.Item>
              <Stack.Item fill>
                <Card>
                  <Card.Section primary>
                    <Stack verticalAlignment="center">
                      <Stack.Item>
                        <Icon name="debug" size="large" color="white" />
                      </Stack.Item>
                      <Stack.Item fill wrap>
                        <Text as="p" weight={600} dataId="Label" color="White">
                          <span id="installation-tracker-debugger-help">
                            {t('apps.details.installation.debugger.label')}
                          </span>
                        </Text>
                      </Stack.Item>
                      <Stack.Item>
                        <Button
                          text={t('apps.details.installation.debugger.button')}
                          onClick={() => window.open(`/analytics/#/${app.id}/settings/debugger/`, '_blank')}
                        />
                      </Stack.Item>
                    </Stack>
                  </Card.Section>
                </Card>
              </Stack.Item>
            </Stack>
          </Stack.Item>
        </Stack>
      </Layout>
    </Title>
  );
};

export default connector(withInject<InjectedProps>({
  Title: 'TitleCrate.components.Title',
  containerSettings: 'ContainerSettingsCrate.ContainerSettings',
  routerService: 'RouterCrate.router',
  organization: 'OrganizationCrate.organization',
})(
  withTranslation('administration')(errorBoundary(Installation)),
));
